import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { graphql } from 'react-apollo';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  Dimmer, Loader, Message, Table,
} from 'semantic-ui-react';

import '../assets/theme.css';

import GuaranteePurchasesListV2 from '../views/GuaranteePurchases/ListV2';
import Search from '../views/Shared/Search';
import Pagination from '../views/Shared/Pagination';
import GuaranteePurchaseEdit from '../views/GuaranteePurchases/EditForm';
import GuaranteePurchaseExtend from '../views/GuaranteePurchases/ExtendForm';
import GuaranteePurchaseCancel from '../views/GuaranteePurchases/CancelForm';
import AuditModal from '../components/AuditModal';
import client from '../apollo';
import useExternalPurchasesList from '../hooks/useExternalPurchasesList';
import externalGuaranteePurchasesQuery from '../graphql/externalGuaranteePurchasesQuery';
import guaranteePurchasesQuery from '../graphql/guaranteePurchasesQuery';
import guaranteePurchaseAuditsQuery from '../graphql/guaranteePurchaseAuditsQuery';
import updateGuaranteePurchaseMutation from '../graphql/updateGuaranteePurchaseMutation';
import cancelGuaranteePurchaseMutation from '../graphql/cancelGuaranteePurchaseMutation';
import APP_PATH from '../constants/paths';
import { DEFAULT_PAGE_SIZE } from '../constants';

const ACTION_UNAVAILABLE = 'This action is unavailable for this purchase.';

const MODAL_BY_ACTION = {
  Edit: 'edit',
  'Extend Guarantee': 'extend',
  'Cancel Guarantee': 'cancel',
};

const buildVariables = ({
  page, categoryCode, vin, dealer,
}) => ({
  page,
  limit: DEFAULT_PAGE_SIZE,
  categoryCode,
  vin,
  dealer,
});

const renderAuditRow = auditRecord => {
  return (
    <>
      {auditRecord.event === 'update'
        && `${auditRecord.versionAuthor} updated the Guarantee Purchase - ${
          auditRecord.itemType
        } details`}
      {auditRecord.event === 'create'
        && `${auditRecord.versionAuthor} created the Guarantee Purchase - ${
          auditRecord.itemType
        }`}
      {auditRecord.event === 'destroy'
        && `${auditRecord.versionAuthor} Destroyed Guarantee Purchase - ${
          auditRecord.itemType
        }`}
    </>
  );
};

const renderChangesetRow = (auditRecord, change, key) => {
  if (key !== 'updated_at'
    && auditRecord.event === 'update'
    && change[1] !== null
    && change[1] !== '') {
    return (
      <Table.Row key={key}>
        <Table.Cell>
          <span>
            set{' '}
            <span style={{ fontWeight: 'bold' }}>
              {key}{' '}
            </span>
            from{' '}
            <span style={{ fontWeight: 'bold' }}>
              {' '}
              {change[0] || 'nil'}{' '}
            </span>
            to{' '}
            <span style={{ fontWeight: 'bold' }}>
              {change[1]}{' '}
            </span>
          </span>
        </Table.Cell>
      </Table.Row>
    );
  }
  return null;
};

const GuaranteePurchasesV2 = ({
  searchFields, history, updateMutation, cancelMutation,
}) => {
  const {
    items,
    error,
    refetch,
    start,
    end,
    isLastPage,
    isInitialLoading,
    isPaginationLoading,
    nextHandler,
    previousHandler,
    onSearchSubmit,
  } = useExternalPurchasesList({
    query: externalGuaranteePurchasesQuery,
    responseField: 'externalGuaranteePurchases',
    buildVariables,
  });

  const [openModal, setOpenModal] = useState(null);
  const [legacyPurchase, setLegacyPurchase] = useState(null);
  const [auditPurchaseId, setAuditPurchaseId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [flashMessage, setFlashMessage] = useState(null);
  const [formError, setFormError] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const handleMessageDismiss = useCallback(() => {
    setFlashMessage(null);
  }, []);

  const closeModal = useCallback(() => {
    setOpenModal(null);
    setLegacyPurchase(null);
    setAuditPurchaseId(null);
    setFormError(null);
    setFormSubmitting(false);
  }, []);

  const fetchLegacyPurchase = useCallback(async row => {
    const { vin, externalId } = row;
    if (!vin || !externalId) return null;

    const result = await client.query({
      query: guaranteePurchasesQuery,
      variables: { vin },
      fetchPolicy: 'network-only',
    });
    const purchases = (result.data && result.data.guaranteePurchases) || [];

    return (
      purchases.find(
        purchase => purchase.guaranteeOffer
          && purchase.guaranteeOffer.externalId === externalId
      ) || null
    );
  }, []);

  const onSelectGuarantee = useCallback(
    async(action, row) => {
      setActionError(null);

      if (action === 'Audit') {
        if (!row.guaranteePurchaseId) {
          setActionError(ACTION_UNAVAILABLE);
          return;
        }
        setAuditPurchaseId(row.guaranteePurchaseId);
        setOpenModal('audit');
        return;
      }

      if (action === 'Vehicle Status Tracker') {
        if (!row.guaranteePurchaseId) {
          setActionError(ACTION_UNAVAILABLE);
          return;
        }
        history.push(
          APP_PATH.VEHICLE_STATUS_TRACKER.replace(
            ':purchaseId',
            row.guaranteePurchaseId
          )
        );
        return;
      }

      const modal = MODAL_BY_ACTION[action];
      if (!modal) return;

      if (!row.guaranteePurchaseId) {
        setActionError(ACTION_UNAVAILABLE);
        return;
      }

      try {
        const record = await fetchLegacyPurchase(row);
        if (!record) {
          setActionError(ACTION_UNAVAILABLE);
          return;
        }
        setFormError(null);
        setFormSubmitting(false);
        setLegacyPurchase(record);
        setOpenModal(modal);
      } catch (fetchError) {
        setActionError(ACTION_UNAVAILABLE);
      }
    },
    [fetchLegacyPurchase, history]
  );

  const submitUpdate = useCallback(
    async(values, successMessage, errorMessage) => {
      setFormSubmitting(true);
      try {
        const { data } = await updateMutation({
          variables: { purchaseInput: { guaranteePurchase: { ...values } } },
        });
        const { success, errors } = data.updateGuaranteePurchase;
        if (!success) {
          setFormSubmitting(false);
          setFormError(errors);
          return;
        }
        setFlashMessage(successMessage);
        closeModal();
        refetch();
      } catch (mutationError) {
        setFormSubmitting(false);
        setFormError(errorMessage);
      }
    },
    [closeModal, refetch, updateMutation]
  );

  const onEditFormSubmitHandler = useCallback(
    values => submitUpdate(
      values,
      'Successfully Updated Guarantee Purchase',
      'Error while updating Guarantee Purchase'
    ),
    [submitUpdate]
  );

  const onExtendFormSubmitHandler = useCallback(
    values => submitUpdate(
      values,
      'Successfully extended Guarantee Purchase',
      'Error while extending Guarantee Purchase'
    ),
    [submitUpdate]
  );

  const onCancelFormSubmitHandler = useCallback(
    async values => {
      setFormSubmitting(true);
      try {
        const { data } = await cancelMutation({
          variables: { cancelInput: { ...values } },
        });
        const { success, errors } = data.cancelGuaranteePurchase;
        if (!success) {
          setFormSubmitting(false);
          setFormError(errors);
          return;
        }
        setFlashMessage('Successfully Cancelled Guarantee Purchase');
        closeModal();
        refetch();
      } catch (mutationError) {
        setFormSubmitting(false);
        setFormError('Error while cancelling Guarantee Purchase');
      }
    },
    [closeModal, refetch, cancelMutation]
  );

  return (
    <div>
      {error && (
        <Message
          negative
          content={error.message || 'An error occurred while loading data.'}
        />
      )}
      {actionError && <Message negative content={actionError} />}
      {flashMessage && (
        <Message
          onDismiss={handleMessageDismiss}
          content={flashMessage}
          positive
        />
      )}
      <Search
        onSubmit={onSearchSubmit}
        searchFields={searchFields}
        searchOptions={['vin', 'dealerNumbers']}
        multipleSearches
      />
      <Dimmer.Dimmable dimmed={isInitialLoading}>
        <Dimmer active={isInitialLoading} inverted>
          <Loader data-testid='loading-overlay'>Loading...</Loader>
        </Dimmer>

        <GuaranteePurchasesListV2
          onSelectGuarantee={onSelectGuarantee}
          items={items}
        />

        <Pagination
          start={start}
          end={end}
          nextHandler={nextHandler}
          previousHandler={previousHandler}
          loading={isPaginationLoading}
          lastPage={isLastPage}
          pageSize={DEFAULT_PAGE_SIZE}
        />
      </Dimmer.Dimmable>

      {legacyPurchase && openModal === 'edit' && (
        <GuaranteePurchaseEdit
          isModalOpen
          onCloseHandler={closeModal}
          onSubmitHandler={onEditFormSubmitHandler}
          guaranteePurchase={legacyPurchase}
          error={formError}
          submitting={formSubmitting}
        />
      )}

      {legacyPurchase && openModal === 'extend' && (
        <GuaranteePurchaseExtend
          isModalOpen
          onCloseHandler={closeModal}
          onSubmitHandler={onExtendFormSubmitHandler}
          guaranteePurchase={legacyPurchase}
          error={formError}
          submitting={formSubmitting}
        />
      )}

      {legacyPurchase && openModal === 'cancel' && (
        <GuaranteePurchaseCancel
          isCancelModalOpen
          onCloseHandler={closeModal}
          onSubmitHandler={onCancelFormSubmitHandler}
          guaranteePurchase={legacyPurchase}
          error={formError}
          submitting={formSubmitting}
        />
      )}

      {auditPurchaseId && openModal === 'audit' && (
        <AuditModal
          header='Status History - Guarantee Purchases'
          fetchAuditRecordQuery={guaranteePurchaseAuditsQuery}
          queryVariables={{ guaranteePurchaseId: parseInt(auditPurchaseId, 10) }}
          auditResponseAttribute='guaranteePurchaseAudits'
          renderAuditRow={renderAuditRow}
          renderChangesetRow={renderChangesetRow}
          isAuditModalOpen
          onCloseHandler={closeModal}
        />
      )}
    </div>
  );
};

GuaranteePurchasesV2.propTypes = {
  searchFields: PropTypes.shape({
    vin: PropTypes.string,
    dealerNumbers: PropTypes.array,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  updateMutation: PropTypes.func,
  cancelMutation: PropTypes.func,
};

GuaranteePurchasesV2.defaultProps = {
  updateMutation: null,
  cancelMutation: null,
};

const mapStateToProps = state => {
  const {
    data: { searchFields },
  } = state;
  return { searchFields };
};

export default compose(
  withRouter,
  connect(mapStateToProps),
  graphql(updateGuaranteePurchaseMutation, { name: 'updateMutation' }),
  graphql(cancelGuaranteePurchaseMutation, { name: 'cancelMutation' })
)(GuaranteePurchasesV2);
